import { useRef, useState, type ReactNode } from "react";
import { apiBase, logout } from "../api";
import { useI18n } from "../i18n";
import { Confirm, Modal } from "../ui";
import { ALLOW_INSECURE_LOCAL, DEFAULT_SERVER, isNativeRuntime } from "./platform";
import { secureTokenStore } from "./secureToken";
import { probeServer, saveServerUrl, validateServerUrl } from "./serverUrl";

/**
 * Hidden "Server" setting: long-press (≈1.5 s) the logo → confirmation → server address with validation and a
 * connection test. Saving signs the device out (the token belongs to the old server) and restarts the web view.
 * In a browser this wrapper renders its children only.
 */
export function ServerLongPress({ children }: { children: ReactNode }) {
  const [stage, setStage] = useState<"idle" | "confirm" | "edit">("idle");
  const timer = useRef<number | null>(null);
  if (!isNativeRuntime()) return <>{children}</>;
  const start = () => { stop(); timer.current = window.setTimeout(() => setStage("confirm"), 1500); };
  const stop = () => { if (timer.current) window.clearTimeout(timer.current); timer.current = null; };
  return (
    <>
      <span className="server-press" onPointerDown={start} onPointerUp={stop} onPointerLeave={stop} onPointerCancel={stop} onContextMenu={(e) => e.preventDefault()}>{children}</span>
      {stage !== "idle" && <ServerDialog stage={stage} onConfirm={() => setStage("edit")} onClose={() => setStage("idle")} />}
    </>
  );
}

export function ServerDialog({ stage, onConfirm, onClose }: { stage: "confirm" | "edit"; onConfirm: () => void; onClose: () => void }) {
  const { t } = useI18n();
  const [value, setValue] = useState(apiBase() || DEFAULT_SERVER);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  if (stage === "confirm") return <Confirm open title={t("mob.server.confirmTitle")} body={t("mob.server.confirm")} confirmLabel={t("common.next")} onConfirm={onConfirm} onCancel={onClose} />;
  const check = () => {
    const v = validateServerUrl(value, { allowInsecureLocal: ALLOW_INSECURE_LOCAL });
    if (!v.ok) setMsg({ ok: false, text: t(("mob.server.err." + v.error) as "mob.server.err.https") });
    return v;
  };
  const test = async () => {
    const v = check(); if (!v.ok) return null;
    setBusy(true);
    const ok = await probeServer(v.url);
    setBusy(false);
    setMsg(ok ? { ok: true, text: t("mob.server.ok", { host: new URL(v.url).host }) } : { ok: false, text: t("mob.server.fail") });
    return ok ? v.url : null;
  };
  const apply = async (url: string | null) => {
    setBusy(true);
    // the session belongs to the old server: end it there (also forgets this device's push token), then drop it
    await Promise.race([logout().catch(() => undefined), new Promise((r) => setTimeout(r, 4000))]);
    await secureTokenStore.set(null).catch(() => undefined);
    await saveServerUrl(url);
    window.location.replace("/");
  };
  return (
    <Modal open title={t("mob.server.title")} onClose={onClose} footer={<>
      <button className="btn btn-quiet" onClick={() => void apply(null)} disabled={busy}>{t("mob.server.reset")}</button>
      <button className="btn btn-soft" onClick={() => void test()} disabled={busy}>{t("mob.server.test")}</button>
      <button className="btn btn-primary" disabled={busy} onClick={() => void test().then((u) => { if (u) void apply(u === DEFAULT_SERVER ? null : u); })}>{t("mob.server.save")}</button>
    </>}>
      <div className="stack">
        <p className="muted small">{t("mob.server.current", { url: (apiBase() || DEFAULT_SERVER) + (apiBase() === DEFAULT_SERVER ? " (" + t("mob.server.default") + ")" : "") })}</p>
        <label className="field"><span className="label">{t("mob.server.url")}</span>
          <input type="url" inputMode="url" autoCapitalize="off" autoCorrect="off" spellCheck={false} value={value} onChange={(e) => { setValue(e.target.value); setMsg(null); }} placeholder="https://" /></label>
        <p className="hint">{t("mob.server.hint")}</p>
        {msg && <div className={"note" + (msg.ok ? " note-info" : "")} role={msg.ok ? "status" : "alert"}>{msg.text}</div>}
      </div>
    </Modal>
  );
}
